import { CheckoutShell } from '@/components/CheckoutShell';
import { t, useT } from '@/i18n/t';

interface Props {
  // The human-readable failure reason, surfaced verbatim from the API/SDK so the
  // visitor (and the publisher debugging it) sees why checkout could not start.
  message: string;
  // When set, renders a "Try again" button that re-runs the failed step. Omitted
  // when there is nothing to retry.
  onRetry?: () => void;
  // When set, the surface is dismissible (close button + Escape). A blocking gate
  // omits it so the error stays put.
  onDismiss?: () => void;
}

// ErrorStep is the shared checkout failure surface. It replaces the old silent
// close — a failed startCheckout used to unmount the surface (or bounce a
// logged-in visitor to the email form), leaving the reason only in the console —
// with a visible, friendly explanation and an optional retry.
export function ErrorStep({ message, onRetry, onDismiss }: Props) {
  useT();
  return (
    <CheckoutShell ariaLabel={t('errorStep.ariaLabel')} currentStep={1} hideSteps onDismiss={onDismiss}>
      <div class="sm-eyebrow sm-eyebrow-icon sm-eyebrow-icon-danger">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="12" y1="8" x2="12" y2="13" />
          <line x1="12" y1="16.5" x2="12.01" y2="16.5" />
        </svg>
        {t('errorStep.eyebrow')}
      </div>
      <h2 class="sm-title">{t('errorStep.title')}</h2>
      <p class="sm-subtitle" role="alert">{message}</p>

      {onRetry && (
        <button class="sm-btn sm-btn-primary" type="button" onClick={onRetry}>
          {t('checkout.tryAgain')}
        </button>
      )}
    </CheckoutShell>
  );
}
